// src/components/ConfiguracoesNotificacoes.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import '../styles/ConfiguracoesNotificacoes.css';
import defaultLogo from '../assets/logo.png';

// O TEMPLATE_CONFIG permanece o mesmo da versão anterior
const TEMPLATE_CONFIG = {
  welcomeEmail: {
    name: "Boas-vindas ao Novo Colaborador",
    description: "O primeiro e-mail que um novo colaborador recebe ao ser cadastrado.",
    placeholders: [ { key: "{{userName}}", description: "Nome do novo colaborador." } ],
    defaultSubject: "Bem-vindo(a) ao Hub Gestão!",
    defaultBody: `<p>Olá, {{userName}}!</p><p>Seja muito bem-vindo(a) ao <strong>Hub Gestão</strong>! Estamos entusiasmados por ter você conosco.</p><p>Para começar, acesse a plataforma clicando no botão abaixo e faça o login. Se for o seu primeiro acesso, pode usar a opção "Esqueceu a senha?" para definir uma nova.</p>`,
    button: { text: "Acessar a Plataforma", url: "https://hubgestao.web.app" }
  },
  resultDelivered: {
    name: "Resultados da Avaliação",
    description: "E-mail enviado ao colaborador quando os seus resultados finais são liberados.",
    placeholders: [
      { key: "{{userName}}", description: "Nome do colaborador." },
      { key: "{{cycleName}}", description: "Nome do ciclo de avaliação." }
    ],
    defaultSubject: "🎉 Seus resultados de avaliação estão disponíveis!",
    defaultBody: `<p>Olá, {{userName}},</p><p>Ótima notícia! Os resultados do ciclo de avaliação de desempenho "<strong>{{cycleName}}</strong>" já foram liberados para si.</p><p>Acesse a plataforma para conferir os seus feedbacks e a sua nota final.</p>`,
    button: { text: "Ver Meus Resultados", url: "https://hubgestao.web.app/meus-resultados" }
  },
  newEvaluationTask: {
    name: "Nova Tarefa de Avaliação",
    description: "Notifica um colaborador que ele tem uma nova avaliação para responder.",
    placeholders: [
        { key: "{{evaluatorName}}", description: "Nome de quem vai responder." },
        { key: "{{evaluatedName}}", description: "Nome de quem está a ser avaliado." },
        { key: "{{cycleName}}", description: "Nome do ciclo." },
    ],
    defaultSubject: "Você tem uma nova avaliação para responder",
    defaultBody: `<p>Olá, {{evaluatorName}}.</p><p>Uma nova avaliação, parte do ciclo "<strong>{{cycleName}}</strong>", está disponível para você responder sobre <strong>{{evaluatedName}}</strong>.</p><p>Por favor, acesse a plataforma para concluir esta tarefa.</p>`,
    button: { text: "Responder Agora", url: "https://hubgestao.web.app/avaliacoes/minhas-tarefas" }
  },
  newFeedback: {
    name: "Novo Feedback Recebido",
    description: "Notifica um colaborador que ele recebeu um novo feedback.",
    placeholders: [
        { key: "{{recipientName}}", description: "Nome de quem recebe o feedback." },
        { key: "{{senderName}}", description: "Nome de quem enviou o feedback." },
    ],
    defaultSubject: "Você recebeu um novo feedback!",
    defaultBody: `<p>Olá, {{recipientName}}.</p><p>Você recebeu um novo feedback de <strong>{{senderName}}</strong>. Acesse a plataforma para visualizar.</p>`,
    button: { text: "Ver Feedback", url: "https://hubgestao.web.app/dashboard" }
  },
  feedbackRequest: {
    name: "Solicitação de Feedback",
    description: "Notifica um colaborador que alguém solicitou o seu feedback.",
    placeholders: [
        { key: "{{recipientName}}", description: "Nome de quem precisa dar o feedback." },
        { key: "{{requesterName}}", description: "Nome de quem pediu o feedback." },
    ],
    defaultSubject: "{{requesterName}} solicitou o seu feedback",
    defaultBody: `<p>Olá, {{recipientName}}.</p><p><strong>{{requesterName}}</strong> gostaria de receber o seu feedback.</p>`,
    button: { text: "Responder Solicitação", url: "https://hubgestao.web.app/dashboard" }
  },
  moodAlert: {
    name: "Alerta de Humor (para Líderes)",
    description: "Notifica um líder quando um membro da sua equipa regista um humor baixo.",
    placeholders: [
        { key: "{{leaderName}}", description: "Nome do líder." },
        { key: "{{userName}}", description: "Nome do colaborador." },
        { key: "{{mood}}", description: "O humor que foi registado." },
    ],
    defaultSubject: "Alerta de Humor: {{userName}}",
    defaultBody: `<p>Olá, {{leaderName}}.</p><p>Um membro da sua equipa, <strong>{{userName}}</strong>, registou o humor como "<strong>{{mood}}</strong>".</p><p>Considere fazer um check-in para oferecer suporte, se necessário.</p>`,
    button: { text: "Ver Equipa", url: "https://hubgestao.web.app/minha-equipe" }
  },
  // =========================================================================
  // === NOVO TEMPLATE ADICIONADO AQUI =======================================
  // =========================================================================
  evaluationReminder: {
    name: "Lembrete de Avaliação Pendente",
    description: "Enviado para colaboradores que ainda não responderam a uma avaliação atribuída.",
    placeholders: [
      { key: "{{userName}}", description: "Nome do colaborador que precisa responder." },
      { key: "{{cycleName}}", description: "Nome do ciclo de avaliação." },
      { key: "{{endDate}}", description: "Data de término do ciclo." }
    ],
    defaultSubject: "Lembrete: Você tem uma avaliação pendente",
    defaultBody: `<p>Olá, {{userName}}.</p><p>Este é um lembrete amigável de que você ainda tem uma avaliação pendente para responder no ciclo "<strong>{{cycleName}}</strong>".</p><p>O prazo para conclusão é <strong>{{endDate}}</strong>. A sua participação é muito importante!</p>`,
    button: { text: "Responder Agora", url: "https://hubgestao.web.app/avaliacoes/minhas-tarefas" }
  },
};

const EmailPreview = ({ subject, bodyHtml, button, color, logoUrl }) => {
    const processedBody = bodyHtml
        .replace(/{{userName}}/g, 'Nome do Colaborador').replace(/{{cycleName}}/g, 'Avaliação Trimestral')
        .replace(/{{evaluatorName}}/g, 'Nome do Avaliador').replace(/{{evaluatedName}}/g, 'Nome do Avaliado')
        .replace(/{{recipientName}}/g, 'Nome do Destinatário').replace(/{{senderName}}/g, 'Nome do Remetente')
        .replace(/{{requesterName}}/g, 'Nome do Solicitante').replace(/{{leaderName}}/g, 'Nome do Líder')
        .replace(/{{mood}}/g, 'Triste')
        // Adicionada a variável para o novo template
        .replace(/{{endDate}}/g, '31/12/2025');

    const processedSubject = subject
        .replace(/{{userName}}/g, 'Nome do Colaborador')
        .replace(/{{requesterName}}/g, 'Nome do Solicitante');


    return (
        <div className="preview-pane">
            <h4>Pré-visualização do E-mail</h4>
            <div className="email-preview-container">
                <div className="email-header" style={{ backgroundColor: color }}>
                    <img src={logoUrl || defaultLogo} alt="Logo da Empresa" />
                </div>
                <div className="email-subject"><strong>Assunto:</strong> {processedSubject}</div>
                <div className="email-body" dangerouslySetInnerHTML={{ __html: processedBody }} />
                {button && (
                    <div className="email-button-container">
                        <a href="#!" className="email-button" style={{ backgroundColor: color }}>{button.text}</a>
                    </div>
                )}
            </div>
        </div>
    );
};

const NotificationTemplateEditor = ({ empresaId }) => {
  const [currentTemplateId, setCurrentTemplateId] = useState('welcomeEmail');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateColor, setTemplateColor] = useState(''); // <-- NOVO ESTADO PARA A COR
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [visualSettings, setVisualSettings] = useState({ primaryColor: '#8dbefd', logoUrl: '' });

  const currentConfig = TEMPLATE_CONFIG[currentTemplateId];

  const loadTemplateAndVisuals = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setFeedback({ message: '', type: '' });
    try {
      const settingsRef = doc(db, 'Empresas', empresaId, 'settings', 'onboarding');
      const settingsSnap = await getDoc(settingsRef);
      const globalVisuals = settingsSnap.exists() && settingsSnap.data().visuals ? settingsSnap.data().visuals : { primaryColor: '#8dbefd', logoUrl: '' };
      setVisualSettings(globalVisuals);

      const templateRef = doc(db, 'Empresas', empresaId, 'notificationTemplates', currentTemplateId);
      const docSnap = await getDoc(templateRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSubject(data.subject || currentConfig.defaultSubject);
        setBody(data.body || currentConfig.defaultBody);
        setTemplateColor(data.color || globalVisuals.primaryColor); // <-- USA A COR DO TEMPLATE OU A GLOBAL
      } else {
        setSubject(currentConfig.defaultSubject);
        setBody(currentConfig.defaultBody);
        setTemplateColor(globalVisuals.primaryColor); // <-- USA A COR GLOBAL COMO PADRÃO
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setFeedback({ message: 'Falha ao carregar dados.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [empresaId, currentTemplateId, currentConfig]);

  useEffect(() => {
    loadTemplateAndVisuals();
  }, [loadTemplateAndVisuals]);

  const handleSave = async () => {
    if (!empresaId) return;
    setSaving(true);
    setFeedback({ message: '', type: '' });
    try {
      const templateRef = doc(db, 'Empresas', empresaId, 'notificationTemplates', currentTemplateId);
      await setDoc(templateRef, { subject, body, color: templateColor }, { merge: true }); // <-- GUARDA A COR
      setFeedback({ message: 'Template guardado com sucesso!', type: 'success' });
    } catch (error) {
      console.error("Erro ao guardar o template:", error);
      setFeedback({ message: 'Falha ao guardar. Tente novamente.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="template-editor-container">
      <header className="template-editor-header">
        <h2>Editor de Notificações</h2>
        <p>Selecione um template para personalizar o seu conteúdo e pré-visualizar o resultado final.</p>
      </header>
      <div className="editor-layout-with-preview">
        <div className="editor-pane">
            <aside className="template-menu">
            <ul>
                {Object.entries(TEMPLATE_CONFIG).map(([id, config]) => (
                <li key={id}><button onClick={() => setCurrentTemplateId(id)} className={currentTemplateId === id ? 'active' : ''}>{config.name}</button></li>
                ))}
            </ul>
            </aside>
            <main className="template-content">
            {loading ? (<p>A carregar editor...</p>) : (
                <div className="template-card">
                    <h3>{currentConfig.name}</h3>
                    <p className="card-description">{currentConfig.description}</p>
                    
                    <div className="form-group">
                        <label htmlFor="subject">Assunto do E-mail</label>
                        <input type="text" id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
                    </div>

                    {/* --- NOVO CAMPO DE COR --- */}
                    <div className="form-group">
                        <label htmlFor="templateColor">Cor do Cabeçalho e Botão</label>
                        <div className="color-input-wrapper">
                            <input type="color" id="templateColor" value={templateColor} onChange={(e) => setTemplateColor(e.target.value)} />
                            <span>{templateColor}</span>
                            <button className="btn-reset-color" onClick={() => setTemplateColor(visualSettings.primaryColor)}>
                                Usar Cor Principal da Marca
                            </button>
                        </div>
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="body">Corpo do E-mail (HTML)</label>
                        <textarea id="body" value={body} onChange={(e) => setBody(e.target.value)} rows="12" />
                    </div>

                    <div className="placeholders-info">
                        <strong>Variáveis disponíveis:</strong>
                        <ul>{currentConfig.placeholders.map(p => (<li key={p.key}><code>{p.key}</code> - {p.description}</li>))}</ul>
                    </div>

                    <div className="form-actions">
                        <button onClick={handleSave} disabled={saving}>{saving ? 'A guardar...' : 'Guardar Template'}</button>
                        {feedback.message && <span className={`feedback-message ${feedback.type}`}>{feedback.message}</span>}
                    </div>
                </div>
            )}
            </main>
        </div>
        {!loading && (
            <EmailPreview 
                subject={subject}
                bodyHtml={body}
                button={currentConfig.button}
                color={templateColor} // Passa a cor para a pré-visualização
                logoUrl={visualSettings.logoUrl}
            />
        )}
      </div>
    </div>
  );
};

export default NotificationTemplateEditor;